import * as React from 'react';
import { Box, Button, Container, Divider, IconButton, Paper, Select, Skeleton, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CalendarBlank as CalendarBlankIcon, Plus as PlusIcon } from '@phosphor-icons/react/dist/ssr';
import { CaretLeft as CaretLeftIcon } from '@phosphor-icons/react/dist/ssr/CaretLeft';
import { CaretRight as CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight';

import { config } from '@/config';
import axios from '@/lib/v2/axios';
import { dayjs } from '@/lib/v2/dayjs';
import { getApiErrorMessage } from '@/lib/api-error';
import { toast } from '@/components/core/v2/toaster';
import { Seo } from '@/components/seo';
import { CalendarProvider } from '@/components/v2/calendar/calendar-context';
import { CalendarView } from '@/components/v2/calendar/calendar-view';
import { EventDialog, type EventCreateParams } from '@/components/v2/calendar/event-dialog';
import { Option } from '@/components/core/option';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import type { ViewMode } from '@/components/v2/calendar/types';

interface CreateEventParams {
  title: string;
  description: string;
  start_date: Date;
  end_date: Date;
  all_day: boolean;
  type?: string;
}

export function CalendarV2Page(): React.JSX.Element {
  const { view = 'dayGridMonth' } = useExtractSearchParams();
  const queryClient = useQueryClient();

  // Managed here so the header toolbar and CalendarView share date state
  const [date, setDate] = React.useState<Date>(new Date());
  const calendarRef = React.useRef<any>(null);
  
  // Event dialog state
  const [eventDialogOpen, setEventDialogOpen] = React.useState(false);
  const [eventDialogRange, setEventDialogRange] = React.useState<{ start: Date; end: Date } | undefined>();

  const createEventMutation = useMutation({
    mutationFn: async (params: EventCreateParams) => {
      // Transform the params to match the API expected format
      const apiParams = {
        title: params.title,
        description: params.description,
        start_date: params.start,
        end_date: params.end,
        all_day: params.allDay,
        type: params.type,
        priority: params.priority,
        visibility: params.visibility,
        is_recurring: params.isRecurring,
        recurrence_type: params.recurrenceType,
        recurrence_interval: params.recurrenceInterval,
        recurrence_days: params.recurrenceDays,
        recurrence_end_date: params.recurrenceEndDate,
        recurrence_count: params.recurrenceCount,
      };
      const response = await axios.post(`${config.api.base}/api/v2/calendar/calendar-events`, apiParams);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.success('Event created successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to create event'));
    },
  });

  const handleViewChange = (value: ViewMode) => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(value);
    }
  };

  const handleDateToday = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.today();
      setDate(calendarApi.getDate());
    }
  };

  const handleDatePrev = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.prev();
      setDate(calendarApi.getDate());
    }
  };

  const handleDateNext = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.next();
      setDate(calendarApi.getDate());
    }
  };

  const handleNewEvent = () => {
    setEventDialogRange(undefined);
    setEventDialogOpen(true);
  };

  // Return the promise: EventDialog awaits it and only closes on success, so a
  // failed save no longer discards everything the user typed.
  const handleEventCreate = (params: EventCreateParams) => createEventMutation.mutateAsync(params);

  const handleEventDialogClose = () => {
    setEventDialogOpen(false);
    setEventDialogRange(undefined);
  };

  return (
    <>
      <Seo title="Calendar & Events" />
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.paper', minHeight: '100vh' }}>

        {/* ── Header strip ─────────────────────────────── */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2 }}
            >
              {/* Left: title + month navigation */}
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                <IconButton onClick={handleDatePrev} size="small">
                  <CaretLeftIcon size={18} />
                </IconButton>
                <Box sx={{ minWidth: 180, textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.25 }}>
                    {dayjs(date).format('MMMM YYYY')}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.25 }} variant="body2">
                    Community Events
                  </Typography>
                </Box>
                <IconButton onClick={handleDateNext} size="small">
                  <CaretRightIcon size={18} />
                </IconButton>
              </Stack>

              {/* Right: view selector + today + add */}
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Button onClick={handleDateToday} size="small" variant="outlined">
                  Today
                </Button>
                <Select
                  defaultValue={view}
                  onChange={(e) => handleViewChange(e.target.value as ViewMode)}
                  size="small"
                  sx={{ minWidth: 110 }}
                >
                  <Option value="dayGridMonth">Month</Option>
                  <Option value="timeGridWeek">Week</Option>
                  <Option value="timeGridDay">Day</Option>
                  <Option value="listWeek">Agenda</Option>
                </Select>
                <RoleGuard
                  requiredAccountRoles={[
                    ACCOUNT_ROLES.ACCOUNT_ADMIN,
                    ACCOUNT_ROLES.ACCOUNT_MANAGER,
                    ACCOUNT_ROLES.ACCOUNT_OWNER,
                  ]}
                  requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER, COMMUNITY_ROLES.BOARD_MEMBER]}
                >
                  <Button
                    onClick={handleNewEvent}
                    size="small"
                    startIcon={<PlusIcon size={16} />}
                    variant="contained"
                  >
                    New Event
                  </Button>
                </RoleGuard>
              </Stack>
            </Stack>
          </Container>
        </Box>

        {/* ── Body ─────────────────────────────────────── */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 3, overflowX: 'auto' }}>
              <Box sx={{ minWidth: 700 }}>
                <CalendarProvider>
                  <CalendarView
                    calendarRef={calendarRef}
                    onDateChange={setDate}
                    view={view}
                  />
                </CalendarProvider>
              </Box>
            </Box>
          </Paper>
        </Container>

        {/* Event Dialog */}
        <EventDialog
          action="create"
          open={eventDialogOpen}
          onClose={handleEventDialogClose}
          onCreate={handleEventCreate}
          range={eventDialogRange}
        />
      </Box>
    </>
  );
}

function useExtractSearchParams(): { view?: ViewMode } {
  const [searchParams] = useSearchParams();
  return { view: (searchParams.get('view') || undefined) as ViewMode | undefined };
}
